import React, { useState, useEffect, useRef, useId } from 'react';
import AlertForm from './AlertForm';
import { submitSearchLead } from '../services/searchLeadService';
import './AlertPill.css';

const AlertPill = ({ city, pillState, setPillState, searchFilters, existingUser, onClose, compact = false, compactOpen = false, onCompactOpenChange }) => {
  const [submitting, setSubmitting] = useState(false);
  const [cadence, setCadence] = useState('daily');
  const [showCadence, setShowCadence] = useState(false);
  const compactRoot = useRef(null), trigger = useRef(null);
  const panelId = useId();
  useEffect(() => {
    if (!compact || !compactOpen) return undefined;
    const outside = event => { if (!compactRoot.current?.contains(event.target)) onCompactOpenChange?.(false); };
    const escape = event => { if (event.key === 'Escape') { event.stopPropagation(); onCompactOpenChange?.(false); trigger.current?.focus({preventScroll:true}); } };
    document.addEventListener('pointerdown', outside); document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [compact, compactOpen, onCompactOpenChange]);

  const hasUserInfo = existingUser?.name && existingUser?.email;

  const handleDirectSubscribe = async () => {
    setSubmitting(true);
    try {
      await submitSearchLead({ ...existingUser, area: city }, searchFilters, cadence);
      setPillState('success');
      if (compact) onCompactOpenChange?.(false);
    } catch {
      setPillState('form');
    } finally {
      setSubmitting(false);
    }
  };

  if (compact) {
    const close = () => { onCompactOpenChange?.(false); trigger.current?.focus({preventScroll:true}); };
    return <div className="map-alert-control" ref={compactRoot}>
      <button ref={trigger} type="button" className="map-tool-button map-alert-trigger" aria-label="Save search and get property alerts" aria-expanded={compactOpen} aria-controls={panelId} onClick={() => {
        onCompactOpenChange?.(!compactOpen);
        if (pillState === 'prompt') { if (hasUserInfo) setShowCadence(true); else setPillState('form'); }
      }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4"/></svg>
        <span role={pillState === 'success' ? 'status' : undefined}>{pillState === 'success' ? 'Saved' : 'Alerts'}</span>
      </button>
      <section id={panelId} className="map-alert-popover" hidden={!compactOpen} aria-label="Property alerts">
        <header><strong>Save this search</strong><button type="button" aria-label="Close property alerts" onClick={close}>×</button></header>
        {pillState === 'success' ? <p>Search saved. You'll get an email when a matching home hits the market in {city}.</p> : pillState === 'form' ?
          <AlertForm city={city} searchFilters={searchFilters} existingUser={existingUser} cadence={cadence} setCadence={setCadence} onClose={close} onSuccess={() => { setPillState('success'); onCompactOpenChange?.(false); }} /> : showCadence && <>
            <p>New listings in {city}, delivered to your inbox.</p>
            <div className="alert-pill__cadence">{['daily', 'weekly', 'monthly'].map(opt => <label key={opt} className="alert-pill__radio"><input type="radio" name="cadence" value={opt} checked={cadence === opt} onChange={() => setCadence(opt)} />{opt.charAt(0).toUpperCase() + opt.slice(1)}</label>)}</div>
            <button type="button" className="alert-pill__cta" disabled={submitting} onClick={handleDirectSubscribe}>{submitting ? 'Saving…' : 'Save search'}</button>
          </>}
      </section>
    </div>;
  }

  if (pillState === 'success') {
    return (
      <div className="alert-pill alert-pill--success" role="status">
        ✓ Search saved. I'll email you when a matching home hits the market in {city}.
      </div>
    );
  }

  if (pillState === 'form') {
    return (
      <AlertForm
        city={city}
        searchFilters={searchFilters}
        onSuccess={() => setPillState('success')}
        onClose={() => setPillState('prompt')}
        existingUser={existingUser}
        cadence={cadence}
        setCadence={setCadence}
      />
    );
  }

  // Returning visitor clicked "Save Search & Get Alerts" — choose cadence first.
  if (showCadence && hasUserInfo) {
    return (
      <div className="alert-pill" role="alert">
        <span>How often?</span>
        <div className="alert-pill__cadence">
          {['daily', 'weekly', 'monthly'].map(opt => (
            <label key={opt} className="alert-pill__radio">
              <input
                type="radio"
                name="cadence"
                value={opt}
                checked={cadence === opt}
                onChange={() => setCadence(opt)}
              />
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </label>
          ))}
        </div>
        <button
          className="alert-pill__cta"
          disabled={submitting}
          onClick={handleDirectSubscribe}
        >
          {submitting ? '...' : 'Save Search'}
        </button>
      </div>
    );
  }

  return (
    <div className="alert-pill" role="alert">
      {onClose && <button className="alert-pill__close" onClick={onClose} aria-label="Dismiss">×</button>}
      <span>Save this search and get automatic new-listing alerts for {city}.</span>
      <button
        className="alert-pill__cta"
        onClick={hasUserInfo ? () => setShowCadence(true) : () => setPillState('form')}
      >
        Save Search &amp; Get Alerts
      </button>
    </div>
  );
};

export default AlertPill;
